import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// ASA autoimmune patient app (separate arm)
import { AsaAuthProvider, useAsaAuth } from "./asa/AuthContext";
import Landing from "./asa/pages/Landing";
import AsaRegister from "./asa/pages/Register";
import AsaLogin from "./asa/pages/Login";
import ForgotPassword from "./asa/pages/ForgotPassword";
import ResetPassword from "./asa/pages/ResetPassword";
import Dashboard from "./asa/pages/Dashboard";
import SymptomChecker from "./asa/pages/SymptomChecker";
import SymptomDiary from "./asa/pages/SymptomDiary";
import Library from "./asa/pages/Library";
import Support from "./asa/pages/Support";
import Reports from "./asa/pages/Reports";
import Account from "./asa/pages/Account";
import Insights from "./asa/pages/Insights";
import FlarePrediction from "./asa/pages/FlarePrediction";
import Wearables from "./asa/pages/Wearables";
import GpReport from "./asa/pages/GpReport";
import FoodGuide from "./asa/pages/FoodGuide";
import Trials from "./asa/pages/Trials";
import UpgradeScreen from "./asa/components/UpgradeScreen";
import PaymentSuccess from "./asa/pages/PaymentSuccess";
import AdminDashboard from "./asa/pages/AdminDashboard";
import MyRecords from "./asa/pages/MyRecords";
import MyProgress from "./asa/pages/MyProgress";
import Pricing from "./asa/pages/Pricing";
import ResearchObservatory from "./asa/pages/ResearchObservatory";
import { hasFullAccess } from "./asa/data";

function RequireAsa({ children }) {
  const { user, ready } = useAsaAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/asa/login" replace />;
  return children;
}

function RequireTier({ title, children }) {
  const { user, ready } = useAsaAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/asa/login" replace />;
  return hasFullAccess(user) ? children : <UpgradeScreen requiredTierId="pro" title={title} />;
}

function App() {
  useEffect(() => {
    document.title = "ASAL | Autoimmune Symptoms, Awareness & Learning";
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AsaAuthProvider>
          <Routes>
            {/* ASA patient app */}
            <Route path="/" element={<Navigate to="/asa" replace />} />
            <Route path="/asa" element={<Landing />} />
            <Route path="/asa/register" element={<AsaRegister />} />
            <Route path="/asa/login" element={<AsaLogin />} />
            <Route path="/asa/forgot-password" element={<ForgotPassword />} />
            <Route path="/asa/reset-password" element={<ResetPassword />} />
            <Route path="/asa/payment-success" element={<PaymentSuccess />} />
            <Route path="/asa/admin" element={<AdminDashboard />} />
            <Route path="/asa/dashboard" element={<RequireAsa><Dashboard /></RequireAsa>} />
            <Route path="/asa/symptom-checker" element={<RequireAsa><SymptomChecker /></RequireAsa>} />
            <Route path="/asa/diary" element={<RequireAsa><RequireTier title="Symptom Diary"><SymptomDiary /></RequireTier></RequireAsa>} />
            <Route path="/asa/progress" element={<RequireAsa><RequireTier title="My Progress"><MyProgress /></RequireTier></RequireAsa>} />
            <Route path="/asa/records" element={<RequireAsa><RequireTier title="My Records"><MyRecords /></RequireTier></RequireAsa>} />
            <Route path="/asa/insights" element={<RequireAsa><RequireTier title="AI Insights"><Insights /></RequireTier></RequireAsa>} />
            <Route path="/asa/flare-prediction" element={<RequireAsa><RequireTier title="Symptom Forecast"><FlarePrediction /></RequireTier></RequireAsa>} />
            <Route path="/asa/wearables" element={<RequireAsa><RequireTier title="Wearables"><Wearables /></RequireTier></RequireAsa>} />
            <Route path="/asa/gp-report" element={<RequireAsa><RequireTier title="GP Report"><GpReport /></RequireTier></RequireAsa>} />
            <Route path="/asa/trials" element={<RequireAsa><RequireTier title="Clinical Trials"><Trials /></RequireTier></RequireAsa>} />
            <Route path="/asa/library" element={<RequireAsa><Library /></RequireAsa>} />
            <Route path="/asa/food-guide" element={<RequireAsa><FoodGuide /></RequireAsa>} />
            <Route path="/asa/reports" element={<RequireAsa><RequireTier title="Reports & Tools"><Reports /></RequireTier></RequireAsa>} />
            <Route path="/asa/support" element={<RequireAsa><Support /></RequireAsa>} />
            <Route path="/asa/account" element={<RequireAsa><Account /></RequireAsa>} />
            <Route path="/asa/pricing" element={<Pricing />} />
            <Route path="/asa/research-observatory" element={<ResearchObservatory />} />
            {/* Retired routes → send to dashboard */}
            <Route path="/asa/research" element={<Navigate to="/asa/dashboard" replace />} />

            {/* Legacy routes → ASAL */}
            <Route path="/login" element={<Navigate to="/asa/login" replace />} />
            <Route path="*" element={<Navigate to="/asa" replace />} />
          </Routes>
        </AsaAuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
